// @ts-nocheck
import React, { useState, useRef, useEffect } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Image, Video, Loader2, X, BarChart3, LayoutTemplate } from 'lucide-react';
import { CreatePostData } from '@/hooks/useSocialWall';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useLinkPreview } from '@/hooks/useLinkPreview';
import LinkPreviewCard from './LinkPreviewCard';
import StatsShareModal from './StatsShareModal';
import MentionAutocomplete from './MentionAutocomplete';
import { parseMentions } from '@/hooks/useMentionSearch';
import StatsCard from './StatsCard';
import TemplatePickerModal from './TemplatePickerModal';
import TemplatePostForm from './TemplatePostForm';
import { PostTemplate } from './wallPostTemplates';
import { getSessionUser } from '@/lib/authSession';

interface PostComposerProps {
  userAvatar?: string | null;
  userName?: string | null;
  profileId?: string;
  gradYear?: number | string;
  sport?: string;
  position?: string;
  onSubmit: (data: CreatePostData) => void;
  isSubmitting?: boolean;
}

interface MediaPreview {
  file: File;
  url: string;
  type: 'image' | 'video';
}

interface StatsDataType {
  athleteName?: string;
  athleteAvatar?: string;
  gradYear?: number | string;
  sport?: string;
  position?: string;
  stats: { name: string; value: string; category?: string }[];
}

const MAX_IMAGES = 4;
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB

const PostComposer: React.FC<PostComposerProps> = ({
  userAvatar,
  userName,
  profileId,
  gradYear,
  sport,
  position,
  onSubmit,
  isSubmitting = false,
}) => {
  const [content, setContent] = useState('');
  const [mediaPreviews, setMediaPreviews] = useState<MediaPreview[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [statsModalOpen, setStatsModalOpen] = useState(false);
  const [selectedStats, setSelectedStats] = useState<StatsDataType | null>(null);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState<PostTemplate | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  
  const {
    linkPreview,
    detectedUrl,
    isLoading: isLoadingPreview,
    detectAndFetchUrl,
    removePreview,
    reset: resetLinkPreview,
  } = useLinkPreview();

  // Detect URLs as user types
  useEffect(() => {
    detectAndFetchUrl(content);
  }, [content, detectAndFetchUrl]);

  const getInitials = (name: string | null | undefined) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const remainingSlots = MAX_IMAGES - mediaPreviews.filter(m => m.type === 'image').length;
    const filesToAdd = Array.from(files).slice(0, remainingSlots);

    for (const file of filesToAdd) {
      if (file.size > MAX_IMAGE_SIZE) {
        toast({
          title: 'File too large',
          description: `${file.name} exceeds the 10MB limit`,
          variant: 'destructive',
        });
        continue;
      }

      const preview: MediaPreview = {
        file,
        url: URL.createObjectURL(file),
        type: 'image',
      };
      setMediaPreviews(prev => [...prev, preview]);
    }

    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  const handleVideoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Only allow one video at a time
    if (mediaPreviews.some(m => m.type === 'video')) {
      toast({
        title: 'Video limit reached',
        description: 'Only one video per post is allowed',
        variant: 'destructive',
      });
      return;
    }

    if (file.size > MAX_VIDEO_SIZE) {
      toast({
        title: 'File too large',
        description: `Video exceeds the 50MB limit`,
        variant: 'destructive',
      });
      return;
    }

    const preview: MediaPreview = {
      file,
      url: URL.createObjectURL(file),
      type: 'video',
    };
    setMediaPreviews(prev => [...prev, preview]);

    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
  };

  const removeMedia = (index: number) => {
    setMediaPreviews(prev => {
      const removed = prev[index];
      URL.revokeObjectURL(removed.url);
      return prev.filter((_, i) => i !== index);
    });
  };

  const uploadMedia = async (): Promise<string[]> => {
    const uploadedUrls: string[] = [];

    for (const preview of mediaPreviews) {
      const { data: { user } } = await getSessionUser();
      if (!user) throw new Error('Not authenticated');

      const fileExt = preview.file.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('wall-media')
        .upload(fileName, preview.file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('wall-media')
        .getPublicUrl(fileName);

      uploadedUrls.push(publicUrl);
    }

    return uploadedUrls;
  };

  const handleStatsConfirm = (statsData: StatsDataType) => {
    setSelectedStats(statsData);
  };

  const removeStats = () => {
    setSelectedStats(null);
  };

  const handleSelectTemplate = (template: PostTemplate) => {
    setActiveTemplate(template);
  };

  const handleCancelTemplate = () => {
    setActiveTemplate(null);
  };

  const handleTemplateSubmit = (data: {
    template_id: string;
    template_data: Record<string, string>;
    media_urls: string[];
  }) => {
    onSubmit({
      content: '',
      media_urls: data.media_urls.length > 0 ? data.media_urls : undefined,
      post_type: 'media',
      template_id: data.template_id,
      template_data: data.template_data,
    });

    setActiveTemplate(null);
  };

  const handleSubmit = async () => {
    if (!content.trim() && mediaPreviews.length === 0 && !detectedUrl && !selectedStats) return;

    try {
      setIsUploading(true);

      let mediaUrls: string[] = [];
      if (mediaPreviews.length > 0) {
        mediaUrls = await uploadMedia();
      }

      let postType: 'text' | 'media' | 'link' | 'stats' = 'text';
      if (selectedStats) postType = 'stats';
      else if (mediaUrls.length > 0) postType = 'media';
      else if (detectedUrl && linkPreview) postType = 'link';

      onSubmit({
        content: content.trim(),
        media_urls: mediaUrls.length > 0 ? mediaUrls : undefined,
        link_url: detectedUrl && linkPreview ? detectedUrl : undefined,
        link_preview: linkPreview ? {
          title: linkPreview.title,
          description: linkPreview.description,
          image: linkPreview.image,
          domain: linkPreview.domain,
          isYouTube: linkPreview.isYouTube,
          videoId: linkPreview.videoId,
        } : undefined,
        post_type: postType,
        stats_data: selectedStats || undefined,
      });

      // Reset form
      setContent('');
      mediaPreviews.forEach(p => URL.revokeObjectURL(p.url));
      setMediaPreviews([]);
      resetLinkPreview();
      setSelectedStats(null);
    } catch (error) {
      console.error('Error uploading media:', error);
      toast({
        title: 'Upload failed',
        description: 'Failed to upload media. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSubmit();
    }
  };

  const isLoading = isSubmitting || isUploading;
  const imageCount = mediaPreviews.filter(m => m.type === 'image').length;

  // If a template is active, show the template form
  if (activeTemplate) {
    return (
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <Avatar className="h-10 w-10 flex-shrink-0">
              <AvatarImage src={userAvatar || undefined} alt={userName || 'User'} />
              <AvatarFallback className="bg-primary text-primary-foreground">
                {getInitials(userName)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <TemplatePostForm
                template={activeTemplate}
                onCancel={handleCancelTemplate}
                onSubmit={handleTemplateSubmit}
                isSubmitting={isSubmitting}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6">
      <CardContent className="pt-6">
        <div className="flex gap-3">
          <Avatar className="h-10 w-10 flex-shrink-0">
            <AvatarImage src={userAvatar || undefined} alt={userName || 'User'} />
            <AvatarFallback className="bg-primary text-primary-foreground">
              {getInitials(userName)}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1 space-y-3">
            <MentionAutocomplete
              placeholder="What's on your mind? Use @ to mention athletes"
              value={content}
              onChange={setContent}
              onKeyDown={handleKeyDown}
              minHeight="80px"
              disabled={isLoading}
            />

            {/* Media Previews */}
            {mediaPreviews.length > 0 && (
              <div className={`grid gap-2 ${
                mediaPreviews.length === 1 ? 'grid-cols-1' : 
                mediaPreviews.length === 2 ? 'grid-cols-2' : 
                'grid-cols-2'
              }`}>
                {mediaPreviews.map((preview, index) => (
                  <div key={index} className="relative group">
                    {preview.type === 'image' ? (
                      <img
                        src={preview.url}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                    ) : (
                      <video
                        src={preview.url}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => removeMedia(index)}
                      className="absolute top-1 right-1 p-1 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      disabled={isLoading}
                    >
                      <X className="h-4 w-4 text-white" />
                    </button>
                    {preview.type === 'video' && (
                      <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/50 rounded text-xs text-white">
                        Video
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Link Preview */}
            {(isLoadingPreview || linkPreview) && detectedUrl && !selectedStats && (
              <LinkPreviewCard
                preview={linkPreview || { title: null, description: null, image: null, domain: '' }}
                url={detectedUrl}
                onRemove={removePreview}
                isEditing={true}
                isLoading={isLoadingPreview}
              />
            )}

            {/* Stats Preview */}
            {selectedStats && (
              <div className="relative">
                <button
                  type="button"
                  onClick={removeStats}
                  className="absolute top-2 right-2 p-1 bg-background/80 rounded-full z-10 hover:bg-background"
                  disabled={isLoading}
                >
                  <X className="h-4 w-4" />
                </button>
                <StatsCard statsData={selectedStats} />
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <div className="flex gap-2">
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  multiple
                  onChange={handleImageSelect}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-foreground"
                  disabled={isLoading || imageCount >= MAX_IMAGES}
                  onClick={() => imageInputRef.current?.click()}
                  title={imageCount >= MAX_IMAGES ? 'Max 4 images' : 'Add image'}
                >
                  <Image className="h-4 w-4" />
                </Button>
                
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/mp4,video/quicktime"
                  onChange={handleVideoSelect}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-foreground"
                  disabled={isLoading || mediaPreviews.some(m => m.type === 'video')}
                  onClick={() => videoInputRef.current?.click()}
                  title="Add video"
                >
                  <Video className="h-4 w-4" />
                </Button>

                {profileId && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-foreground"
                    disabled={isLoading || !!selectedStats}
                    onClick={() => setStatsModalOpen(true)}
                    title="Share your stats"
                  >
                    <BarChart3 className="h-4 w-4" />
                  </Button>
                )}

                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-foreground"
                  disabled={isLoading}
                  onClick={() => setTemplatePickerOpen(true)}
                  title="Use template"
                >
                  <LayoutTemplate className="h-4 w-4" />
                </Button>
              </div>
              
              <Button
                onClick={handleSubmit}
                disabled={isLoading || (!content.trim() && mediaPreviews.length === 0 && !(detectedUrl && linkPreview) && !selectedStats)}
                size="sm"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {isUploading ? 'Uploading...' : 'Posting...'}
                  </>
                ) : (
                  'Post'
                )}
              </Button>
            </div>
          </div>
        </div>
      </CardContent>

      {/* Stats Share Modal */}
      {profileId && (
        <StatsShareModal
          open={statsModalOpen}
          onOpenChange={setStatsModalOpen}
          profileId={profileId}
          athleteName={userName || undefined}
          athleteAvatar={userAvatar || undefined}
          gradYear={gradYear}
          sport={sport}
          position={position}
          onConfirm={handleStatsConfirm}
        />
      )}

      {/* Template Picker Modal */}
      <TemplatePickerModal
        open={templatePickerOpen}
        onOpenChange={setTemplatePickerOpen}
        onSelectTemplate={handleSelectTemplate}
      />
    </Card>
  );
};

export default PostComposer;